import {
  type FC,
  type KeyboardEvent,
  type ChangeEvent,
  type ClipboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import translate from "src/lib/translate";
import type { TranslationKey } from "src/types/translationKey";
import IconPaperclip from "@9amhealth/shared/icons/IconPaperclip";
import { useActionSheet } from "@9amhealth/shared/src/molecule/ActionSheet";
import type { InternalActionSheetButton } from "@9amhealth/shared/src/molecule/ActionSheet/types";
import { toast } from "src/state/state";
import type { LeapChatAttachment } from "./state/types";
import { DocumentIcon } from "./MessageFiles";
import {
  ComposerRow,
  AddAttachmentButton,
  HeroInputCardWrapper,
  HeroInputCard,
  HeroInput,
  SendButton,
  AttachmentPreviewRow,
  AttachmentPreviewItem,
  AttachmentFileIcon,
  AttachmentRemoveButton,
  AttachmentSpinnerOverlay,
  AttachmentErrorOverlay
} from "./leapChat.styles";

const ArrowUpIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="13"
    height="15"
    fill="none"
    viewBox="0 0 13 15"
  >
    <path
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M6.083 14.083V.75m0 0L.75 6.083M6.083.75l5.334 5.333"
    />
  </svg>
);

const CloseIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// --- File validation ---

const MAX_FILE_SIZE_BYTES = 18 * 1024 * 1024; // 18 MB

const IMAGE_EXTENSIONS = new Set([
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".gif",
  ".heic",
  ".heif"
]);

function isImageFile(file: File): boolean {
  if (file.type.startsWith("image/")) {
    return true;
  }
  const dot = file.name.lastIndexOf(".");
  if (dot === -1) {
    return false;
  }
  return IMAGE_EXTENSIONS.has(file.name.slice(dot).toLowerCase());
}

function isPdfFile(file: File): boolean {
  if (file.type === "application/pdf") {
    return true;
  }
  return file.name.toLowerCase().endsWith(".pdf");
}

type FileValidationMode = "image" | "pdf" | "any";

interface FileValidationError {
  key: TranslationKey;
  variables: Record<string, unknown>;
}

function getFileExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot).toLowerCase();
}

function validateFile(
  file: File,
  mode: FileValidationMode
): FileValidationError | null {
  const isImage = isImageFile(file);
  const isPdf = isPdfFile(file);
  const ext = getFileExtension(file.name);

  if (mode === "image" && !isImage) {
    return {
      key: "leapChat.attachment.invalidImageFileType" as TranslationKey,
      variables: { extension: ext || file.type || "unknown" }
    };
  }
  if (mode === "pdf" && !isPdf) {
    return {
      key: "leapChat.attachment.invalidPdfFileType" as TranslationKey,
      variables: { extension: ext || file.type || "unknown" }
    };
  }
  if (mode === "any" && !isImage && !isPdf) {
    return {
      key: "leapChat.attachment.invalidFileType" as TranslationKey,
      variables: { extension: ext || file.type || "unknown" }
    };
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      key: "leapChat.attachment.fileTooLarge" as TranslationKey,
      variables: { maxSize: "18 MB" }
    };
  }

  return null;
}

const IS_IOS_DEVICE =
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

// --- Component ---

interface ChatComposerProps {
  onSend: () => void;
  placeholder?: string;
  inputValue: string;
  onInputChange: (value: string) => void;
  isRunning: boolean;
  attachments: LeapChatAttachment[];
  onAddFile: (file: File) => void;
  onRemoveAttachment: (key: string) => void;
}

const ChatComposer: FC<ChatComposerProps> = ({
  onSend,
  placeholder = translate("leapChat.placeholder.askAnything" as TranslationKey),
  inputValue,
  onInputChange,
  isRunning,
  attachments,
  onAddFile,
  onRemoveAttachment
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const iosFileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const isTouchDevice = useMemo(
    () => window.matchMedia("(pointer: coarse)").matches,
    []
  );

  const actionSheetButtons = useMemo(() => {
    const buttons: InternalActionSheetButton[] = [];
    if (isTouchDevice && !IS_IOS_DEVICE) {
      buttons.push({
        id: "camera",
        title: translate("leapChat.attachment.takePhoto" as TranslationKey),
        icon: "camera"
      });
    }
    if (!IS_IOS_DEVICE) {
      buttons.push(
        {
          id: "gallery",
          title: translate(
            (isTouchDevice
              ? "leapChat.attachment.selectFromGallery"
              : "leapChat.attachment.selectImage") as TranslationKey
          ),
          icon: "image"
        },
        {
          id: "document",
          title: translate("leapChat.attachment.uploadFile" as TranslationKey),
          icon: "document"
        }
      );
    }
    return buttons;
  }, [isTouchDevice]);

  const { show: showActionSheet } = useActionSheet({
    buttons: actionSheetButtons,
    onSelect: (id) => {
      if (id === "camera") {
        cameraInputRef.current?.click();
      } else if (id === "gallery") {
        galleryInputRef.current?.click();
      } else if (id === "document") {
        pdfInputRef.current?.click();
      }
    }
  });

  const handleAttachmentPress = useCallback(() => {
    if (IS_IOS_DEVICE) {
      iosFileInputRef.current?.click();
    } else {
      void showActionSheet();
    }
  }, [showActionSheet]);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    if (!inputValue) {
      const el = textareaRef.current;
      if (el) {
        el.style.height = "auto";
      }
    }
  }, [inputValue]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey && !isTouchDevice) {
        e.preventDefault();
        onSend();
      }
    },
    [onSend, isTouchDevice]
  );

  const processFiles = useCallback(
    (files: Iterable<File>, mode: FileValidationMode) => {
      for (const file of files) {
        const error = validateFile(file, mode);
        if (error) {
          toast.error(error.key, error.variables);
          continue;
        }
        onAddFile(file);
      }
    },
    [onAddFile]
  );

  const handleImageInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        processFiles(Array.from(e.target.files), "image");
      }
      e.target.value = "";
    },
    [processFiles]
  );

  const handlePdfInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        processFiles(Array.from(e.target.files), "pdf");
      }
      e.target.value = "";
    },
    [processFiles]
  );

  const handleCombinedInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        processFiles(Array.from(e.target.files), "any");
      }
      e.target.value = "";
    },
    [processFiles]
  );

  useEffect(() => {
    const onDragEnter = (e: globalThis.DragEvent) => {
      if (!e.dataTransfer?.types.includes("Files")) {
        return;
      }
      e.preventDefault();
      dragCounter.current++;
      if (dragCounter.current === 1) {
        setIsDragging(true);
      }
    };

    const onDragLeave = () => {
      dragCounter.current--;
      if (dragCounter.current === 0) {
        setIsDragging(false);
      }
    };

    const onDragOver = (e: globalThis.DragEvent) => {
      if (!e.dataTransfer?.types.includes("Files")) {
        return;
      }
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "copy";
      }
    };

    const onDrop = (e: globalThis.DragEvent) => {
      e.preventDefault();
      dragCounter.current = 0;
      setIsDragging(false);
      const files = e.dataTransfer?.files;
      if (files?.length) {
        processFiles(Array.from(files), "any");
      }
    };

    document.addEventListener("dragenter", onDragEnter);
    document.addEventListener("dragleave", onDragLeave);
    document.addEventListener("dragover", onDragOver);
    document.addEventListener("drop", onDrop);
    return () => {
      document.removeEventListener("dragenter", onDragEnter);
      document.removeEventListener("dragleave", onDragLeave);
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("drop", onDrop);
    };
  }, [processFiles]);

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLTextAreaElement>) => {
      const { items } = e.clipboardData;
      if (!items) {
        return;
      }
      const imageFiles: File[] = [];
      for (const item of Array.from(items)) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            imageFiles.push(file);
          }
        }
      }
      if (imageFiles.length > 0) {
        e.preventDefault();
        processFiles(imageFiles, "image");
      }
    },
    [processFiles]
  );

  const hasUploading = attachments.some((a) => a.status === "uploading");
  const hasError = attachments.some((a) => a.status === "error");
  const doneAttachments = attachments.filter((a) => a.status === "done");
  const hasContent = inputValue.trim().length > 0 || doneAttachments.length > 0;
  const sendDisabled = isRunning || !hasContent || hasUploading || hasError;

  return (
    <ComposerRow className="chat-composer">
      <AddAttachmentButton
        type="button"
        aria-label="Add attachment"
        onClick={handleAttachmentPress}
      >
        <IconPaperclip />
      </AddAttachmentButton>
      <HeroInputCardWrapper>
        <HeroInputCard
          style={
            isDragging
              ? { outline: "2px dashed #4a90d9", outlineOffset: "-2px" }
              : undefined
          }
        >
          {attachments.length > 0 && (
            <AttachmentPreviewRow>
              {attachments.map((a) => (
                <AttachmentPreviewItem key={a.key}>
                  {a.previewUrl ? (
                    <img src={a.previewUrl} alt={a.name} />
                  ) : (
                    <AttachmentFileIcon>
                      <DocumentIcon />
                    </AttachmentFileIcon>
                  )}
                  {a.status === "uploading" && <AttachmentSpinnerOverlay />}
                  {a.status === "error" && <AttachmentErrorOverlay />}
                  <AttachmentRemoveButton
                    type="button"
                    aria-label={`Remove ${a.name}`}
                    onClick={() => onRemoveAttachment(a.key)}
                  >
                    <CloseIcon />
                  </AttachmentRemoveButton>
                </AttachmentPreviewItem>
              ))}
            </AttachmentPreviewRow>
          )}
          <HeroInput
            ref={textareaRef}
            rows={1}
            value={inputValue}
            onChange={(e) => {
              onInputChange(e.target.value);
              autoResize();
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            enterKeyHint={isTouchDevice ? "enter" : "send"}
            placeholder={placeholder}
            disabled={isRunning}
          />
          {hasContent && (
            <SendButton
              type="button"
              onClick={onSend}
              disabled={sendDisabled}
              aria-label="Send message"
            >
              <ArrowUpIcon />
            </SendButton>
          )}
        </HeroInputCard>
      </HeroInputCardWrapper>
      {!IS_IOS_DEVICE && (
        <>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={handleImageInput}
            style={{ display: "none" }}
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
            multiple
            onChange={handleImageInput}
            style={{ display: "none" }}
          />
          <input
            ref={pdfInputRef}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            onChange={handlePdfInput}
            style={{ display: "none" }}
          />
        </>
      )}
      {IS_IOS_DEVICE && (
        <input
          ref={iosFileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf,.jpg,.jpeg,.png,.webp,.pdf"
          multiple
          onChange={handleCombinedInput}
          style={{ display: "none" }}
        />
      )}
    </ComposerRow>
  );
};

export default ChatComposer;
